import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

export default function Unsubscribe() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const isValidId = useMemo(() => Number.isInteger(Number(id)) && Number(id) > 0, [id]);

  useEffect(() => {
    let mounted = true;

    const runUnsubscribe = async () => {
      if (!isValidId) {
        if (mounted) {
          setError('Invalid unsubscribe link.');
          setLoading(false);
        }
        return;
      }

      try {
        const response = await fetch(`https://api.insightos.co.uk/leads/unsubscribe/${encodeURIComponent(String(id))}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.success) {
          throw new Error(payload?.message || 'Unable to unsubscribe at this time.');
        }

        if (mounted) {
          setSuccess(true);
        }
      } catch (err) {
        if (mounted) {
          setError(err?.message || 'Unable to unsubscribe at this time.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    runUnsubscribe();

    return () => {
      mounted = false;
    };
  }, [id, isValidId]);

  const handleCloseTab = () => {
    window.close();
    window.setTimeout(() => {
      if (!window.closed) {
        window.history.back();
      }
    }, 150);
  };

  return (
    <div
      className="min-h-screen bg-cover bg-center bg-no-repeat px-4 py-10"
      style={{
        backgroundImage:
          "linear-gradient(135deg, rgba(219,234,254,0.92) 0%, rgba(233,213,255,0.84) 45%, rgba(255,255,255,0.94) 100%), url('/avenorlogobackground.png')",
      }}
    >
      <div className="mx-auto w-full max-w-xl rounded-2xl border border-white/60 bg-white/75 p-6 shadow-[0_24px_70px_rgba(59,130,246,0.22)] backdrop-blur-xl">
        <div className="mb-4 flex items-center gap-3">
          <img src="/avenor.png" alt="InsightOS Avenor" className="h-10 w-10" />
          <h1 className="text-xl font-bold text-slate-900">InsightOS Avenor</h1>
        </div>

        {loading && (
          <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sky-900">
            <div className="flex items-center gap-3">
              <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-sky-300 border-t-sky-700" />
              <span className="font-semibold">Unsubscribing...</span>
            </div>
          </div>
        )}

        {!loading && success && (
          <div className="space-y-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
              <p className="text-lg font-semibold">Unsubscribed</p>
              <p className="mt-1 text-sm">You have been removed from future outreach.</p>
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
              <p className="font-semibold">Redeem Free Credits Here</p>
              <Link to="/" className="mt-2 inline-block text-sm font-semibold text-indigo-700 underline hover:text-indigo-900">
                Go to Home Page
              </Link>
            </div>

            <button
              type="button"
              onClick={handleCloseTab}
              className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
            >
              Close
            </button>
          </div>
        )}

        {!loading && !success && (
          <div className="space-y-4">
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-900">
              <p className="text-lg font-semibold">Unable to Unsubscribe</p>
              <p className="mt-1 text-sm">{error || 'Please try again later.'}</p>
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
              <p className="font-semibold">Redeem Free Credits Here</p>
              <Link to="/" className="mt-2 inline-block text-sm font-semibold text-indigo-700 underline hover:text-indigo-900">
                Go to Home Page
              </Link>
            </div>

            <button
              type="button"
              onClick={handleCloseTab}
              className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
